// client/src/api/api.js
import axios from "axios";

/**
 * Clean + stable API base URL logic.
 * Public deployment should ALWAYS use the Render backend.
 *
 * LOCAL DEV:
 *   - http://localhost:3000  → http://localhost:3001/api
 *
 * PUBLIC DEPLOY:
 *   - https://nolimitsmedia.github.io/checkin-app
 *   - https://checkin.mtgileadfgim.org
 *   → https://checkin-app-backend.onrender.com/api
 */

const DEFAULTS = {
  LOCAL: "http://localhost:3001/api",
  RENDER: "https://checkin-app-backend.onrender.com/api",
};

const isLocalHost =
  window.location.hostname === "localhost" ||
  window.location.hostname === "127.0.0.1";

/* --------------------------------------------------
 * Decide initial baseURL
 * -------------------------------------------------- */
function initialBaseURL() {
  const envUrl = process.env.REACT_APP_API_URL;

  // Respect .env override if present
  if (envUrl) return envUrl;

  // When running on localhost (React dev server)
  if (isLocalHost) {
    return DEFAULTS.LOCAL;
  }

  // Public deployment → ALWAYS use Render backend
  return DEFAULTS.RENDER;
}

const api = axios.create({
  baseURL: initialBaseURL(),
  withCredentials: false,
});

/* --------------------------------------------------
 * Safety: prevent accidental localhost usage on public
 * -------------------------------------------------- */
if (!isLocalHost && /localhost/i.test(api.defaults.baseURL)) {
  api.defaults.baseURL = DEFAULTS.RENDER;
}

/* --------------------------------------------------
 * Kiosk vs Admin Auth Header Logic
 * -------------------------------------------------- */
function isKioskRequest(config) {
  try {
    const full = new URL(config.url, config.baseURL || "");
    return full.pathname.startsWith("/kiosk");
  } catch {
    const u = String(config.url || "").toLowerCase();
    return u.startsWith("/kiosk") || u.includes("/kiosk/");
  }
}

api.interceptors.request.use(
  (config) => {
    const adminToken = localStorage.getItem("token");
    const kioskToken = localStorage.getItem("kioskToken");

    config.headers = config.headers || {};

    if (isKioskRequest(config)) {
      if (kioskToken) {
        config.headers.Authorization = `Bearer ${kioskToken}`;
      } else {
        delete config.headers.Authorization;
      }
    } else if (adminToken) {
      config.headers.Authorization = `Bearer ${adminToken}`;
    }

    return config;
  },
  (error) => Promise.reject(error)
);

/* --------------------------------------------------
 * Auto-logout on 401
 * -------------------------------------------------- */
api.interceptors.response.use(
  (res) => res,
  (error) => {
    if (!error.response) {
      console.error("🔌 Network error — backend unreachable");
    } else if (error.response.status === 401) {
      localStorage.removeItem("token");
      console.info("🔒 Token cleared due to 401.");
    }
    return Promise.reject(error);
  }
);

console.log("API baseURL:", api.defaults.baseURL);

/* --------------------------------------------------
 * Manual dev helpers
 * -------------------------------------------------- */
window.__setApiBase = (url) => {
  localStorage.setItem("API_BASE_URL", url);
  window.location.reload();
};
window.__clearApiBase = () => {
  localStorage.removeItem("API_BASE_URL");
  window.location.reload();
};

export default api;
